import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { createOrder } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ShoppingBag, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { optimizeImage } from "@/lib/images";

const IDF_DEPTS = ["75", "77", "78", "91", "92", "93", "94", "95"];

const Checkout = () => {
  useDocumentTitle("Finaliser ma commande — cartonmove", "Validez votre commande de cartons et faites-vous livrer en Île-de-France.");
  const { items, totals, clear } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    notes: "",
  });

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.full_name.trim()) return "Le nom complet est requis.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Email invalide.";
    if (!/^[0-9 +().-]{8,}$/.test(form.phone)) return "Téléphone invalide.";
    if (!form.address.trim()) return "L'adresse est requise.";
    if (!form.city.trim()) return "La ville est requise.";
    if (!/^\d{5}$/.test(form.postal_code)) return "Code postal invalide (5 chiffres).";
    const dept = form.postal_code.slice(0, 2);
    if (!IDF_DEPTS.includes(dept))
      return "Nous livrons uniquement en Île-de-France (départements 75, 77, 78, 91, 92, 93, 94, 95).";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Votre panier est vide.");
      return;
    }
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer: form,
      };
      const order = await createOrder(payload);
      clear();
      navigate(`/confirmation/${order.id}`, { state: { order } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la création de la commande");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="container-cdm py-20 text-center" data-testid="checkout-empty">
        <ShoppingBag className="h-12 w-12 mx-auto text-[#a8b4bf] mb-4" />
        <h1 className="font-display font-extrabold text-[28px] sm:text-[33px] md:text-[48px] uppercase">Votre panier est vide</h1>
        <Link to="/cartons-demenagement" className="btn-primary mt-6 inline-flex">Découvrir la boutique</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page">
      <div className="container-cdm pt-8">
        <Link to="/cartons-demenagement" className="inline-flex items-center gap-2 text-sm text-[#5b6b7a] hover:text-[#0f1d2b]">
          <ArrowLeft className="h-4 w-4" /> Continuer mes achats
        </Link>
      </div>

      <section className="container-cdm py-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Form */}
        <form onSubmit={handleSubmit} className="lg:col-span-7 space-y-8" data-testid="checkout-form">
          <div>
            <div className="overline">Étape 1/1</div>
            <h1 className="font-display font-extrabold text-[28px] sm:text-[33px] md:text-[48px]  mt-3">
              Finaliser la commande
            </h1>
            <p className="mt-3 text-[#3a4a5a]">
              Renseignez vos coordonnées. Vous serez recontacté(e) pour confirmer la livraison et le paiement.
            </p>
          </div>

          <div className="bg-white border border-[#e6edf3] p-6 space-y-5">
            <h2 className="font-display font-bold text-lg ">Vos coordonnées</h2>
            <div>
              <Label htmlFor="full_name">Nom complet</Label>
              <Input id="full_name" value={form.full_name} onChange={setField("full_name")} placeholder="Jean Dupont" className="mt-1" data-testid="input-name" required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={setField("email")} placeholder="jean@exemple.fr" className="mt-1" data-testid="input-email" required />
              </div>
              <div>
                <Label htmlFor="phone">Téléphone</Label>
                <Input id="phone" value={form.phone} onChange={setField("phone")} placeholder="06 12 34 56 78" className="mt-1" data-testid="input-phone" required />
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#e6edf3] p-6 space-y-5">
            <h2 className="font-display font-bold text-lg ">Adresse de livraison</h2>
            <div>
              <Label htmlFor="address">Adresse</Label>
              <Input id="address" value={form.address} onChange={setField("address")} placeholder="12 rue de la République" className="mt-1" data-testid="input-address" required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <Label htmlFor="postal_code">Code postal</Label>
                <Input id="postal_code" value={form.postal_code} onChange={setField("postal_code")} placeholder="75011" className="mt-1" maxLength={5} data-testid="input-postal" required />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="city">Ville</Label>
                <Input id="city" value={form.city} onChange={setField("city")} placeholder="Paris" className="mt-1" data-testid="input-city" required />
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes (étage, code, instructions…)</Label>
              <Textarea id="notes" value={form.notes} onChange={setField("notes")} placeholder="Code porte, étage, horaires préférés…" className="mt-1" data-testid="input-notes" rows={3} />
            </div>
            <p className="text-xs text-[#5b6b7a]">Nous livrons uniquement en Île-de-France (75, 77, 78, 91, 92, 93, 94, 95).</p>
          </div>

          <div className="bg-zinc-100 border border-[#e6edf3] p-5 text-sm text-[#3a4a5a]">
            <strong>Paiement :</strong> Pour le moment, le paiement en ligne n'est pas activé.
            Vous réglerez directement à la livraison (espèces, CB, virement).
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-14 bg-[#FF7A00] hover:bg-[#E66E00] text-white rounded-sm text-base font-semibold"
            data-testid="checkout-submit-button"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Valider la commande — ${totals.total.toFixed(2).replace(".", ",")} €`}
          </Button>
        </form>

        {/* Summary */}
        <aside className="lg:col-span-5">
          <div className="bg-white border border-[#e6edf3] p-6 sticky top-24" data-testid="order-summary">
            <h2 className="font-display font-bold text-lg  mb-4">Récapitulatif</h2>
            <div className="divide-y divide-zinc-100">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-3 py-3" data-testid={`summary-item-${i.slug}`}>
                  <img src={optimizeImage(i.image, 160)} alt={i.name} loading="lazy" decoding="async" className="h-16 w-16 object-cover bg-[#F3EFE9]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold leading-tight">{i.name}</div>
                    <div className="text-xs text-[#5b6b7a] mt-1">Qté : {i.quantity}</div>
                  </div>
                  <div className="text-sm font-semibold whitespace-nowrap">
                    {(i.price * i.quantity).toFixed(2).replace(".", ",")} €
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 border-t border-[#e6edf3] pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#5b6b7a]">Sous-total</span><span className="font-semibold">{totals.subtotal.toFixed(2).replace(".", ",")} €</span></div>
              <div className="flex justify-between"><span className="text-[#5b6b7a]">Livraison IDF</span><span className="font-semibold">{totals.shipping === 0 ? "Offerte" : `${totals.shipping.toFixed(2).replace(".", ",")} €`}</span></div>
              <div className="flex justify-between border-t border-[#e6edf3] pt-3 mt-3">
                <span className="font-display font-bold text-lg uppercase">Total</span>
                <span className="font-display font-extrabold text-xl" data-testid="summary-total">{totals.total.toFixed(2).replace(".", ",")} €</span>
              </div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};

export default Checkout;
